import { createApp } from 'vue'
import '@/styles/globals.scss'
import '@/styles/tailwind.css'
import App from '@/App.vue'
import router from '@/plugins/router.js'
import dayjs from '@/plugins/dayjs.js'
import theme from '@/plugins/theme.js'
import { createPinia } from 'pinia'
import { i18n } from '@/i18n/index.js'
import { createHead } from '@vueuse/head'
import { useLanguageStore } from '@/store/language.js'
import apiPlugin from '@/plugins/api.js'
import scrollPlugin from '@/plugins/scroll.js'
import localePathPlugin from '@/plugins/localePath.js'
import VCalendar from 'v-calendar'
import * as Sentry from '@sentry/vue'

import { api } from '@/api/index.js'
import { useAuthStore } from '@/store/auth.js'
import NotificationPlugin from '@/plugins/notification.js'
import ToastifyPlugin from '@/plugins/toast.js'
import 'v-calendar/style.css'
import ConsentPlugin, { consent } from '@/plugins/consent.js'
import { loadHotjar } from '@/utils/hotjar.js'
import { IMaskDirective } from 'vue-imask'
import { sanitizedHtml } from '@/directives/sanitized-html.directive.js'
import { vPrimeTooltip } from '@/directives/prime-tooltip.ts'
import FloatingVue from 'floating-vue'
import 'floating-vue/dist/style.css'
import PrimeVue from 'primevue/config'
import ToastService from 'primevue/toastservice'
import AuraCustom from './theme-aura-custom'

import { version } from '../package.json'

window.addEventListener('vite:preloadError', (event) => {
  event?.preventDefault()
  window.location.reload()
})

window.addEventListener('error', (event) => {
  const msg = event?.message ?? ''
  if (msg.includes('Unexpected token') || msg.includes('is not a valid JavaScript MIME type')) {
    window.location.reload()
  }
}, true)

const app = createApp(App)

Sentry.init({
  app,
  dsn: import.meta.env.VITE_SENTRY_DSN,
  environment: import.meta.env.VITE_SENTRY_ENVIRONMENT,
  release: version,
  tracesSampleRate: 0.0
})

app.use(router)
app.use(dayjs)
app.use(apiPlugin)
app.use(VCalendar, {})
const pinia = createPinia()
pinia.use(({ store }) => {
  store.$api = api
})
app.use(pinia)
app.use(i18n)
app.use(theme)
app.use(localePathPlugin, { i18n })
app.use(NotificationPlugin)
app.use(scrollPlugin)
app.use(ConsentPlugin)
app.use(createHead())
if (consent.has(consent.cookies.ANALYTICS)) {
  loadHotjar()
}
app.directive('imask', IMaskDirective)
app.directive('sanitized-html', sanitizedHtml)
app.directive('prime-tooltip', vPrimeTooltip)

app.use(FloatingVue, {
  themes: {
    'prime-tooltip': {
      $extend: 'tooltip',
      distance: 8,
      zIndex: 25
    },
    'rocken-tooltip': {
      $extend: 'tooltip',
      delay: { show: 200, hide: 100 },
      distance: 8
    },
    // Rectangular, left-aligned tooltip for multi-line help hints
    // (the pill 'prime-tooltip' is meant for short single-line labels)
    'info-tooltip': {
      $extend: 'tooltip',
      distance: 8
    }
  }
})

app.use(PrimeVue, {
  theme: {
    preset: AuraCustom,
    options: {
      darkModeSelector: false
    }
  }
})
app.use(ToastService)
app.use(ToastifyPlugin)

const auth = useAuthStore()
auth.setApi(api)

const languageStore = useLanguageStore()
i18n.global.locale.value = languageStore.currentLocale

app.mount('#app')
